import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CpuChipIcon, ChevronDownIcon, PaperAirplaneIcon, CurrencyDollarIcon,
  AdjustmentsHorizontalIcon, PhotoIcon, UserIcon, XMarkIcon, ClockIcon
} from '@heroicons/react/24/outline';
import { ModelType, AspectRatioLabels, Resolution, ResolutionLabels, IProjectAsset, getLabelsByLocale, Locale } from '@packages/global';
import { useModelStore } from '../../../stores/modelStore';
import { CompactModelSelector } from '../../common/form';
import AttachmentCard from './AttachmentCard';
import StyleSelector from './StyleSelector';
import { ScriptIdeaSelectedModels } from '../hooks/useScriptIdea';

const SCRIPT_IDEA_PRICE = 5;

interface ScriptIdeaTabProps {
  scriptContent: string;
  onScriptContentChange: (v: string) => void;
  loading: boolean;
  novelFileName: string;
  novelUploading: boolean;
  novelFileInputRef: React.RefObject<HTMLInputElement>;
  onNovelFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearNovelFile: () => void;
  selectedModels: ScriptIdeaSelectedModels;
  onModelsChange: (models: ScriptIdeaSelectedModels) => void;
  selectedSubjects: IProjectAsset[];
  onOpenSubjectSelector: () => void;
  onRemoveSubject: (id: string) => void;
  selectedStyle: string;
  onStyleChange: (s: string) => void;
  aspectRatio: string;
  onAspectRatioChange: (v: string) => void;
  resolution: Resolution;
  onResolutionChange: (v: Resolution) => void;
  maxShotDuration: number | null;
  onMaxShotDurationChange: (v: number | null) => void;
  hasEnoughCoins: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
}

export default function ScriptIdeaTab({
  scriptContent, onScriptContentChange, loading,
  novelFileName, novelUploading, novelFileInputRef, onNovelFileUpload, onClearNovelFile,
  selectedModels, onModelsChange,
  selectedSubjects, onOpenSubjectSelector, onRemoveSubject,
  selectedStyle, onStyleChange,
  aspectRatio, onAspectRatioChange,
  resolution, onResolutionChange,
  maxShotDuration, onMaxShotDurationChange,
  hasEnoughCoins, canSubmit, onSubmit, onKeyPress
}: ScriptIdeaTabProps) {
  const { getModelsByType } = useModelStore();
  const { t, i18n } = useTranslation(['script', 'common']);
  const aspectRatioLabels = getLabelsByLocale(AspectRatioLabels, i18n.language as Locale);
  const resolutionLabels = getLabelsByLocale(ResolutionLabels, i18n.language as Locale);
  const llmModels = getModelsByType(ModelType.LLM);
  const llmVlModels = getModelsByType(ModelType.LLM_VL);
  const text2imageModels = getModelsByType(ModelType.TEXT_TO_IMAGE);
  const imageEditModels = getModelsByType(ModelType.IMAGE_EDIT);

  const [showModelSelector, setShowModelSelector] = React.useState(false);
  const [showAspectRatioSelector, setShowAspectRatioSelector] = React.useState(false);
  const [showResolutionSelector, setShowResolutionSelector] = React.useState(false);
  const [showMaxShotDurationSelector, setShowMaxShotDurationSelector] = React.useState(false);
  const modelSelectorRef = useRef<HTMLDivElement>(null);
  const aspectRatioRef = useRef<HTMLDivElement>(null);
  const resolutionRef = useRef<HTMLDivElement>(null);

  const hasMultipleModels = llmModels.length > 1 || llmVlModels.length > 1 || text2imageModels.length > 1 || imageEditModels.length > 1;

  return (
    <>
      <input ref={novelFileInputRef} type="file" accept=".txt,.md,.docx" onChange={onNovelFileUpload} className="hidden" />

      <div className="flex items-start gap-3 pt-1">
        <AttachmentCard
          fileName={novelFileName}
          uploading={novelUploading}
          disabled={loading}
          required={false}
          iconType="document"
          label={t('idea.multiEpisodeScript')}
          tooltip={t('idea.multiEpisodeTooltip')}
          onUploadClick={() => novelFileInputRef.current?.click()}
          onClear={onClearNovelFile}
        />
        <textarea
          value={scriptContent}
          onChange={(e) => onScriptContentChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) onKeyPress(e as any); }}
          placeholder={t('idea.scriptPlaceholder')}
          className="flex-1 bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-base resize-none h-24 py-0"
          disabled={loading}
          style={{ opacity: loading ? 0.6 : 1 }}
        />
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 flex-wrap">
          {hasMultipleModels && (
            <div className="relative" ref={modelSelectorRef}>
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
              >
                <CpuChipIcon className="w-3.5 h-3.5 text-green-400" />
                <span>{t('idea.model')}</span>
                <ChevronDownIcon className="w-3 h-3 text-gray-400" />
              </button>
              {showModelSelector && (
                <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] p-4 min-w-[320px]">
                  <div className="space-y-3">
                    {llmModels.length > 1 && (
                      <CompactModelSelector label={t('idea.llmModel')} models={llmModels} value={selectedModels.llmModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, llmModelId: id })} />
                    )}
                    {llmVlModels.length > 1 && (
                      <CompactModelSelector label={t('idea.visionLlmModel')} models={llmVlModels} value={selectedModels.llmVlModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, llmVlModelId: id })} />
                    )}
                    {text2imageModels.length > 1 && (
                      <CompactModelSelector label={t('idea.imageGenModel')} models={text2imageModels} value={selectedModels.text2imageModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, text2imageModelId: id })} />
                    )}
                    {imageEditModels.length > 1 && (
                      <CompactModelSelector label={t('idea.imageEditModel')} models={imageEditModels} value={selectedModels.imageEditModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, imageEditModelId: id })} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={onOpenSubjectSelector}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
          >
            <UserIcon className="w-3.5 h-3.5 text-primary" />
            <span>{selectedSubjects.length > 0 ? t('idea.subjectWithCount', { count: selectedSubjects.length }) : t('idea.subject')}</span>
          </button>

          <StyleSelector selectedStyle={selectedStyle} onStyleChange={onStyleChange} disabled={loading} />

          {/* 单个分镜最大时长 */}
          <div className="relative">
            <button
              onClick={() => setShowMaxShotDurationSelector(!showMaxShotDurationSelector)}
              disabled={loading}
              className={`flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-[13px] rounded-lg border transition-colors disabled:opacity-50 ${maxShotDuration ? 'text-amber-400 border-amber-400/40' : 'text-gray-300 border-border-subtle'}`}
            >
              <ClockIcon className="w-3.5 h-3.5 text-amber-400" />
              <span>{maxShotDuration ? t('videoGeneration.maxShotDurationSeconds', { value: maxShotDuration }) : t('videoGeneration.maxShotDuration')}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showMaxShotDurationSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[120px]">
                {[null, 2.5, 5, 7.5, 10, 12.5, 15].map((val) => (
                  <button
                    key={val ?? 'no-limit'}
                    onClick={() => { onMaxShotDurationChange(val); setShowMaxShotDurationSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${maxShotDuration === val ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}
                  >
                    {val === null ? t('videoGeneration.maxShotDurationNoLimit') : t('videoGeneration.durationSecondsSuffix', { value: val })}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={aspectRatioRef}>
            <button
              onClick={() => setShowAspectRatioSelector(!showAspectRatioSelector)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
            >
              <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-purple-400" />
              <span>{aspectRatio}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showAspectRatioSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[160px]">
                {Object.entries(aspectRatioLabels).map(([value, label]) => (
                  <button key={value} onClick={() => { onAspectRatioChange(value); setShowAspectRatioSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${aspectRatio === value ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={resolutionRef}>
            <button
              onClick={() => setShowResolutionSelector(!showResolutionSelector)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
            >
              <PhotoIcon className="w-3.5 h-3.5 text-blue-400" />
              <span>{resolutionLabels[resolution]}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showResolutionSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[140px]">
                {Object.entries(resolutionLabels).map(([value, label]) => (
                  <button key={value} onClick={() => { onResolutionChange(value as Resolution); setShowResolutionSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${resolution === value ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-3 py-2 text-[14px] rounded-lg ${hasEnoughCoins ? 'text-gray-400' : 'text-red-400'}`}>
            <CurrencyDollarIcon className="w-4 h-4" />
            <span>{SCRIPT_IDEA_PRICE}</span>
          </div>
          <button
            onClick={onSubmit}
            disabled={loading || !canSubmit || !hasEnoughCoins}
            className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary-dark hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={!hasEnoughCoins ? t('idea.insufficientCoins', { amount: SCRIPT_IDEA_PRICE }) : ''}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
            )}
          </button>
        </div>
      </div>

      {selectedSubjects.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-subtle">
          <div className="flex items-center gap-2 flex-wrap">
            {selectedSubjects.map((asset) => (
              <div key={asset.id} className="flex items-center gap-1 px-2 py-1 bg-bg-elevated rounded-lg text-xs text-gray-300">
                {asset.conceptImageUrl ? (
                  <img src={asset.conceptImageUrl} alt={asset.name} className="w-4 h-4 object-cover rounded" />
                ) : (
                  <UserIcon className="w-4 h-4 text-gray-400" />
                )}
                <span className="truncate max-w-16">{asset.name}</span>
                <button onClick={() => onRemoveSubject(asset.id)} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
